import styled, { css } from 'styled-components'

import { OptionCheckbox, TypeStyle, untilMd } from '@syconium/little-miss-figgy'

export const PlacementPickerWrapper = styled.div`
  ${untilMd} {
    padding: ${o => o.theme.spacing(6, 0, 0)};
  }
`

export const PlacementInstructions = styled.p`
  ${TypeStyle.css.bodyMicro}
  color: ${o => o.theme.color.text.secondary.on.background};
`

export const PlacementList = styled.div<{ $hideInstructions?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(3)};
  margin-top: ${o => (o.$hideInstructions === true ? o.theme.spacing(5) : o.theme.spacing(3))};
`

export const PlacementCardShell = styled.div<{
  $selected?: boolean
  $disabled?: boolean
  $invalid?: boolean
}>`
  background-color: ${o => o.theme.color.fill.background};
  border-radius: ${o => o.theme.spacing(2)};
  border-style: solid;
  border-color: ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  border-width: 1px;

  ${o =>
    o.$selected &&
    css`
      border-color: ${o.theme.color.stroke.neutral.border.interaction.primary.on.background};
    `}

  ${o =>
    o.$invalid &&
    css`
      border-color: ${o.theme.color.text.critical.on.background};
    `}

  ${o =>
    o.$disabled &&
    css`
      background-color: ${o.theme.color.fill.subtle};
      border-color: transparent;
      border-width: 1px;
    `}
`

export const PlacementCardHeader = styled.div`
  align-items: center;
  display: flex;
  padding: ${o => o.theme.spacing(3.5, 4)};

  /* The checkbox's own ring wraps its padded label; draw it around the box instead. */
  label:has(input[data-focus-visible]) {
    outline: none;

    > input + div {
      outline: 2px solid ${o => o.theme.color.effect.focus.outline.on.background};
      outline-offset: 2px;
    }
  }
`

/**
 * The label is padded and the box sits inside it, so the input is laid over the box alone, for a
 * screen reader's cursor to box what is drawn. 6px = the label's padding.
 *
 * The box's 2px nudge (which lines it up with the label text it normally sits beside) is dropped,
 * so the header's `align-items: center` centers it on the placement text.
 */
export const PlacementCheckbox = styled(OptionCheckbox)`
  position: relative;

  && input + div {
    top: 0;
  }

  && input {
    clip: auto;
    height: 16px;
    inset: 6px auto auto 0;
    margin: 0;
    opacity: 1;
    width: 16px;
  }
`

export const PlacementCardHeaderButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  gap: ${o => o.theme.spacing(2)};
  justify-content: space-between;
  padding: ${o => o.theme.spacing(3.5, 4)};
  text-align: start;
  width: 100%;

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`

export const PlacementLabel = styled.span<{ $disabled?: boolean }>`
  ${TypeStyle.css.bodyMicro}
  color: ${o =>
    o.$disabled
      ? o.theme.color.stroke.disabled.text.on.background
      : o.theme.color.text.primary.on.background};
  font-weight: ${o => o.theme.typography.font.weight[700]};
`

export const PlacementStatus = styled.span`
  ${TypeStyle.css.bodyMicro}
  color: ${o => o.theme.color.text.tertiary.on.background};
  font-weight: ${o => o.theme.typography.font.weight[700]};
`

export const DisclosureToggle = styled.button`
  align-items: center;
  align-self: stretch;
  background: none;
  border: none;
  color: ${o => o.theme.color.text.primary.on.background};
  cursor: pointer;
  display: flex;
  flex: 1;
  justify-content: space-between;
  padding: 0;
  text-align: start;

  &:focus-visible {
    outline: 2px solid ${o => o.theme.color.effect.focus.outline.on.background};
    outline-offset: 2px;
  }
`

export const PlacementHeaderText = styled.span`
  flex: 1;
`

export const CaratSlot = styled.span`
  align-items: center;
  display: flex;
  padding: ${o => o.theme.spacing(1)};
`

export const PlacementCardBody = styled.div`
  border-top: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  margin: 0 ${o => o.theme.spacing(4)};
  padding: ${o => o.theme.spacing(4)} 0;
`
